import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../auth/session';

interface ReputationSnapshot {
  no_show_count: number;
  total_completed: number;
  last_no_show_at: string | null;
  tier: 'green' | 'yellow' | 'red';
  blacklisted: boolean;
  guaranteed: boolean;
  requires_sms_confirmation: boolean;
  requires_card_guarantee: boolean;
}

interface BadgeResult {
  badge_code: string;
  badge_name: string;
  badge_description: string;
  badge_icon: string;
  earned: boolean;
  earned_at: string | null;
  progress: number;
  target: number;
}

interface WalletSnapshot {
  balance_cents: number;
  total_earned_cents: number;
  total_spent_cents: number;
  active_fiches: Array<{ id: number; amount_cents: number; expires_at: string }>;
  recent_transactions: Array<{
    id: number;
    type: string;
    amount_cents: number;
    reason: string | null;
    created_at: string;
  }>;
}

interface ReviewableReservation {
  reservation_id: number;
  store_id: number;
  reserved_at: string;
  guest_name: string;
  store_name: string | null;
  already_reviewed: boolean;
}

interface MyReview {
  id: number;
  store_id: number;
  reservation_id: number;
  rating_food: number;
  rating_service: number;
  rating_ambience: number;
  rating_overall: number;
  comment: string | null;
  xp_awarded: number;
  created_at: string;
}

interface HistoryEntry {
  id: number;
  type: 'booking' | 'checkin' | 'treatment' | 'class' | 'visit' | 'cancel' | 'no_show' | 'loyalty' | 'coupon' | 'notification' | 'preference_update' | 'review';
  vertical: string;
  store_name: string;
  category_code: string | null;
  description: string;
  amount_cents: number | null;
  status: string | null;
  created_at: string;
}

interface EndUserNotification {
  id: number;
  title: string;
  body: string;
  type: string;
  is_read: boolean;
  created_at: string;
}

interface EndUserCoupon {
  id: number;
  code: string;
  description: string | null;
  discount_type: 'fixed' | 'percent';
  discount_value: number;
  status: 'active' | 'used' | 'expired' | 'revoked';
  expires_at: string | null;
  created_at: string;
}

const HISTORY_LABEL: Record<string, string> = {
  booking: 'Prenotazione',
  checkin: 'Check-in',
  treatment: 'Trattamento',
  class: 'Corso',
  visit: 'Visita',
  cancel: 'Cancellazione',
  no_show: 'No-show',
  loyalty: 'Loyalty',
  coupon: 'Coupon',
  notification: 'Notifica',
  preference_update: 'Preferenze',
  review: 'Recensione',
};

const HISTORY_ICON: Record<string, string> = {
  booking: '🍽️',
  checkin: '🏋️',
  treatment: '💇',
  class: '🏃',
  visit: '✓',
  cancel: '✕',
  no_show: '⚠️',
  loyalty: '🪙',
  coupon: '🎟️',
  notification: '�',
  preference_update: '⚙️',
  review: '⭐',
};

const TIER_INFO: Record<string, { label: string; color: string; desc: string }> = {
  green: {
    label: 'GREEN',
    color: '#22c55e',
    desc: 'Prenotazioni garantite dalla piattaforma. Zero attrito.',
  },
  yellow: {
    label: 'YELLOW',
    color: '#eab308',
    desc: 'Conferma SMS richiesta 2 ore prima della prenotazione.',
  },
  red: {
    label: 'RED',
    color: '#ef4444',
    desc: 'Prenotazioni bloccate. Richiede carta garanzia per sbloccare.',
  },
};

const BADGE_ICONS: Record<string, string> = {
  star: '★',
  compass: '🧭',
  globe: '🌍',
  heart: '♥',
  sun: '☀',
  moon: '☾',
  utensils: '🍴',
  pen: '✎',
};

function StarRating({ value, onChange, disabled }: { value: number; onChange: (v: number) => void; disabled?: boolean }) {
  return (
    <div className="sk-star-rating">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={disabled}
          className={`sk-star ${star <= value ? 'sk-star--active' : ''}`}
          onClick={() => onChange(star)}
        >
          {star <= value ? '★' : '☆'}
        </button>
      ))}
    </div>
  );
}

export function EndUserDashboardPage() {
  const [reputation, setReputation] = useState<ReputationSnapshot | null>(null);
  const [badges, setBadges] = useState<BadgeResult[]>([]);
  const [wallet, setWallet] = useState<WalletSnapshot | null>(null);
  const [reviewable, setReviewable] = useState<ReviewableReservation[]>([]);
  const [myReviews, setMyReviews] = useState<MyReview[]>([]);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [filterVertical, setFilterVertical] = useState<string>('');
  const [filterEventType, setFilterEventType] = useState<string>('');
  const [notifications, setNotifications] = useState<EndUserNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [coupons, setCoupons] = useState<EndUserCoupon[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Review form state
  const [reviewingReservation, setReviewingReservation] = useState<ReviewableReservation | null>(null);
  const [reviewForm, setReviewForm] = useState({ food: 0, service: 0, ambience: 0, comment: '' });
  const [submitting, setSubmitting] = useState(false);
  const [reviewMsg, setReviewMsg] = useState('');

  const loadAll = useCallback(async () => {
    try {
      const [repRes, badgeRes, walletRes, revRes, myRevRes, histRes, notifRes, couponRes, unreadRes] = await Promise.all([
        apiFetch('/api/v1/end-user/reputation'),
        apiFetch('/api/v1/end-user/badges'),
        apiFetch('/api/v1/end-user/wallet'),
        apiFetch('/api/v1/end-user/reviews/reviewable'),
        apiFetch('/api/v1/end-user/reviews'),
        apiFetch(`/api/v1/end-user/history?limit=20${filterVertical ? `&vertical=${filterVertical}` : ''}${filterEventType ? `&event_type=${filterEventType}` : ''}`),
        apiFetch('/api/v1/end-user/notifications?limit=10'),
        apiFetch('/api/v1/end-user/coupons'),
        apiFetch('/api/v1/end-user/notifications/unread-count'),
      ]);
      if (repRes.ok) setReputation(await repRes.json());
      if (badgeRes.ok) {
        const bd = await badgeRes.json();
        setBadges(bd.badges || []);
      }
      if (walletRes.ok) setWallet(await walletRes.json());
      if (revRes.ok) setReviewable(await revRes.json());
      if (myRevRes.ok) {
        const mr = await myRevRes.json();
        setMyReviews(mr.reviews || []);
      }
      if (histRes.ok) {
        const hd = await histRes.json();
        setHistory(hd.entries || []);
      }
      if (notifRes.ok) {
        const nd = await notifRes.json();
        setNotifications(nd.notifications || []);
      }
      if (couponRes.ok) {
        const cd = await couponRes.json();
        setCoupons(cd.coupons || []);
      }
      if (unreadRes.ok) {
        const ud = await unreadRes.json();
        setUnreadCount(ud.unread || 0);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore caricamento dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const submitReview = async () => {
    if (!reviewingReservation) return;
    if (reviewForm.food === 0 || reviewForm.service === 0 || reviewForm.ambience === 0) {
      setReviewMsg('Assegna un voto a tutti i 3 parametri');
      return;
    }
    setSubmitting(true);
    setReviewMsg('');
    try {
      const res = await apiFetch('/api/v1/end-user/reviews', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reservation_id: reviewingReservation.reservation_id,
          store_id: reviewingReservation.store_id,
          rating_food: reviewForm.food,
          rating_service: reviewForm.service,
          rating_ambience: reviewForm.ambience,
          comment: reviewForm.comment || undefined,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setReviewMsg(`Recensione pubblicata! +${data.xp_awarded} XP`);
        setReviewingReservation(null);
        setReviewForm({ food: 0, service: 0, ambience: 0, comment: '' });
        loadAll();
      } else {
        const err = await res.json();
        setReviewMsg(err.message || 'Errore nella pubblicazione');
      }
    } catch {
      setReviewMsg('Errore di connessione');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <section className="sk-page sk-page--fill">
        <p className="sk-muted">Caricamento dashboard…</p>
      </section>
    );
  }

  if (error) {
    return (
      <section className="sk-page sk-page--fill">
        <p className="sk-error">{error}</p>
      </section>
    );
  }

  const tierInfo = reputation ? TIER_INFO[reputation.tier] : null;

  return (
    <section className="sk-page sk-page--fill">
      <h1 className="sk-page__title">Il mio Passaporto StrikerPos</h1>
      <p className="sk-page__lead">
        La tua identità cross-tenant: reputation, badge e fiches.
      </p>

      <div className="sk-enduser-dashboard">
        {/* Reputation */}
        {reputation && tierInfo && (
          <div className="sk-card sk-card--reputation" style={{ borderColor: tierInfo.color }}>
            <h2 className="sk-card__title">Reputation</h2>
            <div className="sk-card__body">
              <div className="sk-tier-display" style={{ background: tierInfo.color }}>
                <span className="sk-tier-display__label">{tierInfo.label}</span>
              </div>
              <p className="sk-tier-desc">{tierInfo.desc}</p>
              <div className="sk-stats-row">
                <div className="sk-stat">
                  <span className="sk-stat__value">{reputation.total_completed}</span>
                  <span className="sk-stat__label">Prenotazioni completate</span>
                </div>
                <div className="sk-stat">
                  <span className="sk-stat__value">{reputation.no_show_count}</span>
                  <span className="sk-stat__label">No-show</span>
                </div>
              </div>
              {reputation.guaranteed && (
                <p className="sk-badge sk-badge--green">✓ Prenotazioni Garantite</p>
              )}
              {reputation.requires_sms_confirmation && (
                <p className="sk-badge sk-badge--yellow">⚠ Conferma SMS richiesta</p>
              )}
              {reputation.requires_card_guarantee && (
                <p className="sk-badge sk-badge--red">⚠ Carta garanzia richiesta</p>
              )}
            </div>
          </div>
        )}

        {/* Wallet */}
        {wallet && (
          <div className="sk-card sk-card--wallet">
            <h2 className="sk-card__title">Le mie Fiches</h2>
            <div className="sk-card__body">
              <div className="sk-wallet-balance-display">
                <span className="sk-wallet-amount">{(wallet.balance_cents / 100).toFixed(2)}</span>
                <span className="sk-wallet-currency">€</span>
              </div>
              <p className="sk-muted">Buoni sconto spendibili in qualsiasi locale del circuito</p>
              <div className="sk-stats-row">
                <div className="sk-stat">
                  <span className="sk-stat__value">{(wallet.total_earned_cents / 100).toFixed(2)}€</span>
                  <span className="sk-stat__label">Guadagnato</span>
                </div>
                <div className="sk-stat">
                  <span className="sk-stat__value">{(wallet.total_spent_cents / 100).toFixed(2)}€</span>
                  <span className="sk-stat__label">Speso</span>
                </div>
                <div className="sk-stat">
                  <span className="sk-stat__value">{wallet.active_fiches.length}</span>
                  <span className="sk-stat__label">Fiches attive</span>
                </div>
              </div>
              {wallet.active_fiches.length > 0 && (
                <div className="sk-fiches-list">
                  <p className="sk-muted sk-muted--small">Fiches attive (scadono dopo 90 giorni):</p>
                  {wallet.active_fiches.map((f) => (
                    <div key={f.id} className="sk-fiche-item">
                      <span>{f.amount_cents} cents</span>
                      <span className="sk-muted">scade {new Date(f.expires_at).toLocaleDateString('it-IT')}</span>
                    </div>
                  ))}
                </div>
              )}
              {wallet.recent_transactions.length > 0 && (
                <details>
                  <summary>Storico transazioni</summary>
                  <ul className="sk-tx-list">
                    {wallet.recent_transactions.map((tx) => (
                      <li key={tx.id} className="sk-tx-item">
                        <span className={`sk-tx-type sk-tx-type--${tx.type}`}>{tx.type}</span>
                        <span>{tx.amount_cents > 0 ? '+' : ''}{tx.amount_cents} cents</span>
                        <span className="sk-muted">{tx.reason || ''}</span>
                        <span className="sk-muted">{new Date(tx.created_at).toLocaleString('it-IT')}</span>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
              {/* Coupon attivi nel wallet (blueprint: coupon visibili nel wallet) */}
              {coupons.filter((c) => c.status === 'active').length > 0 && (
                <div style={{ marginTop: '0.75rem', padding: '0.5rem 0.75rem', borderRadius: '0.5rem', background: '#dcfce7', fontSize: '0.8rem' }}>
                  <strong>Coupon attivi: {coupons.filter((c) => c.status === 'active').length}</strong>
                  <span style={{ marginLeft: '0.5rem', color: 'var(--sk-muted)' }}>
                    ({coupons.filter((c) => c.status === 'active').map((c) => c.code).join(', ')})
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Badges */}
        {badges.length > 0 && (
          <div className="sk-card sk-card--badges">
            <h2 className="sk-card__title">
              Badge ({badges.filter((b) => b.earned).length}/{badges.length})
            </h2>
            <div className="sk-card__body sk-badges-grid">
              {badges.map((b) => (
                <div
                  key={b.badge_code}
                  className={`sk-badge-card ${b.earned ? 'sk-badge-card--earned' : 'sk-badge-card--locked'}`}
                >
                  <div className="sk-badge-card__icon">
                    {b.earned ? BADGE_ICONS[b.badge_icon] || '★' : '🔒'}
                  </div>
                  <p className="sk-badge-card__name">{b.badge_name}</p>
                  <p className="sk-badge-card__desc">{b.badge_description}</p>
                  <p className="sk-badge-card__progress">
                    {b.earned ? 'Sbloccato' : `${b.progress}/${b.target}`}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Reviews — Reviewable + My Reviews */}
        <div className="sk-card sk-card--reviews">
          <h2 className="sk-card__title">Recensioni Verificate</h2>
          <div className="sk-card__body">
            {reviewMsg && <p className="sk-success">{reviewMsg}</p>}

            {/* Review form */}
            {reviewingReservation && (
              <div className="sk-review-form">
                <h3>Recensisci: {reviewingReservation.store_name || `Store #${reviewingReservation.store_id}`}</h3>
                <p className="sk-muted sk-muted--small">
                  Prenotazione del {new Date(reviewingReservation.reserved_at).toLocaleString('it-IT')}
                </p>
                <div className="sk-review-field">
                  <label>Qualità del Cibo</label>
                  <StarRating value={reviewForm.food} onChange={(v) => setReviewForm({ ...reviewForm, food: v })} />
                </div>
                <div className="sk-review-field">
                  <label>Servizio e Staff</label>
                  <StarRating value={reviewForm.service} onChange={(v) => setReviewForm({ ...reviewForm, service: v })} />
                </div>
                <div className="sk-review-field">
                  <label>Ambiente e Atmosfera</label>
                  <StarRating value={reviewForm.ambience} onChange={(v) => setReviewForm({ ...reviewForm, ambience: v })} />
                </div>
                <div className="sk-review-field">
                  <label>Commento (opzionale)</label>
                  <textarea
                    value={reviewForm.comment}
                    onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                    maxLength={2000}
                    rows={3}
                    className="sk-input sk-textarea"
                    placeholder="Racconta la tua esperienza…"
                  />
                </div>
                <p className="sk-muted sk-muted--small">+50 XP per il tuo Passaporto StrikerPos</p>
                <div className="sk-review-form__actions">
                  <button
                    onClick={submitReview}
                    disabled={submitting}
                    className="sk-btn sk-btn--primary"
                  >
                    {submitting ? 'Pubblicazione…' : 'Pubblica Recensione'}
                  </button>
                  <button
                    onClick={() => {
                      setReviewingReservation(null);
                      setReviewForm({ food: 0, service: 0, ambience: 0, comment: '' });
                    }}
                    className="sk-btn sk-btn--neutral"
                  >
                    Annulla
                  </button>
                </div>
              </div>
            )}

            {/* Reviewable list */}
            {!reviewingReservation && reviewable.filter((r) => !r.already_reviewed).length > 0 && (
              <div className="sk-reviewable-list">
                <h3>Prenotazioni da recensire ({reviewable.filter((r) => !r.already_reviewed).length})</h3>
                {reviewable
                  .filter((r) => !r.already_reviewed)
                  .map((r) => (
                    <div key={r.reservation_id} className="sk-reviewable-item">
                      <div>
                        <p className="sk-reviewable-item__store">{r.store_name || `Store #${r.store_id}`}</p>
                        <p className="sk-muted sk-muted--small">
                          {new Date(r.reserved_at).toLocaleDateString('it-IT')} · +50 XP
                        </p>
                      </div>
                      <button
                        onClick={() => setReviewingReservation(r)}
                        className="sk-btn sk-btn--primary sk-btn--small"
                      >
                        Recensisci
                      </button>
                    </div>
                  ))}
              </div>
            )}

            {/* My reviews */}
            {myReviews.length > 0 && (
              <div className="sk-my-reviews">
                <h3>Le mie recensioni ({myReviews.length})</h3>
                {myReviews.map((rv) => (
                  <div key={rv.id} className="sk-my-review-item">
                    <div className="sk-my-review-item__header">
                      <span className="sk-my-review-item__store">Store #{rv.store_id}</span>
                      <span className="sk-my-review-item__date">
                        {new Date(rv.created_at).toLocaleDateString('it-IT')}
                      </span>
                    </div>
                    <div className="sk-my-review-item__ratings">
                      <span>🍴 {rv.rating_food}</span>
                      <span>👤 {rv.rating_service}</span>
                      <span>🏠 {rv.rating_ambience}</span>
                      <span className="sk-my-review-item__overall">Overall: {rv.rating_overall}</span>
                    </div>
                    {rv.comment && <p className="sk-my-review-item__comment">"{rv.comment}"</p>}
                    <span className="sk-badge sk-badge--yellow">+{rv.xp_awarded} XP</span>
                  </div>
                ))}
              </div>
            )}

            {!reviewingReservation &&
              reviewable.filter((r) => !r.already_reviewed).length === 0 &&
              myReviews.length === 0 && (
                <p className="sk-muted">
                  Nessuna prenotazione completata da recensire ancora. Completa una prenotazione per sbloccare le recensioni!
                </p>
              )}
          </div>
        </div>

        {/* Notifiche in-app */}
        {notifications.length > 0 && (
          <div className="sk-card sk-card--notifications">
            <h2 className="sk-card__title">
              Notifiche
              {unreadCount > 0 && (
                <span style={{
                  display: 'inline-block',
                  marginLeft: '0.5rem',
                  padding: '0.1rem 0.5rem',
                  borderRadius: '9999px',
                  background: '#ef4444',
                  color: '#fff',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                }}>
                  {unreadCount}
                </span>
              )}
            </h2>
            <div className="sk-card__body">
              <ul className="sk-enduser-feed">
                {notifications.map((n) => (
                  <li
                    key={`notif-${n.id}`}
                    className="sk-enduser-feed__item"
                    style={{ opacity: n.is_read ? 0.6 : 1 }}
                  >
                    <span className="sk-enduser-feed__icon">
                      {n.is_read ? '✓' : '🔔'}
                    </span>
                    <div className="sk-enduser-feed__body">
                      <p className="sk-enduser-feed__action">
                        <strong>{n.title}</strong>
                      </p>
                      <p className="sk-enduser-feed__meta">
                        {n.body}
                        {' · '}
                        {new Date(n.created_at).toLocaleString('it-IT')}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Coupon */}
        {coupons.length > 0 && (
          <div className="sk-card sk-card--coupons">
            <h2 className="sk-card__title">I miei coupon</h2>
            <div className="sk-card__body">
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                {coupons.map((c) => (
                  <div
                    key={`coupon-${c.id}`}
                    style={{
                      padding: '0.75rem 1rem',
                      borderRadius: '0.5rem',
                      border: c.status === 'active' ? '2px dashed #16a34a' : '1px solid var(--sk-border)',
                      background: c.status === 'active' ? '#dcfce7' : 'var(--sk-bg-alt, #f9fafb)',
                      opacity: c.status === 'active' ? 1 : 0.5,
                      minWidth: '180px',
                    }}
                  >
                    <div style={{ fontWeight: 700, fontSize: '1.1rem', fontFamily: 'monospace' }}>
                      {c.code}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--sk-muted)' }}>
                      {c.description || ''}
                    </div>
                    <div style={{ fontSize: '0.85rem', marginTop: '0.25rem' }}>
                      {c.discount_type === 'percent'
                        ? `${c.discount_value}% di sconto`
                        : `€${(c.discount_value / 100).toFixed(2)} di sconto`}
                    </div>
                    <div style={{ fontSize: '0.7rem', marginTop: '0.25rem', color: 'var(--sk-muted)' }}>
                      {c.status === 'active' ? 'Attivo' : c.status === 'used' ? 'Utilizzato' : c.status}
                      {c.expires_at ? ` · scade ${new Date(c.expires_at).toLocaleDateString('it-IT')}` : ''}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Storico cross-verticale */}
        {history.length > 0 && (
          <div className="sk-card sk-card--history">
            <h2 className="sk-card__title">Il mio storico</h2>
            <div className="sk-card__body">
              {/* Filtri timeline (blueprint: filtrabile per verticale + tipo) */}
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
                <select
                  value={filterVertical}
                  onChange={(e) => setFilterVertical(e.target.value)}
                  style={{ fontSize: '0.8rem', padding: '0.25rem 0.5rem', borderRadius: '0.25rem', border: '1px solid var(--sk-border)' }}
                >
                  <option value="">Tutti i verticali</option>
                  <option value="restaurant">Restaurant</option>
                  <option value="beauty">Beauty</option>
                  <option value="gym">Gym</option>
                  <option value="platform">Platform</option>
                </select>
                <select
                  value={filterEventType}
                  onChange={(e) => setFilterEventType(e.target.value)}
                  style={{ fontSize: '0.8rem', padding: '0.25rem 0.5rem', borderRadius: '0.25rem', border: '1px solid var(--sk-border)' }}
                >
                  <option value="">Tutti gli eventi</option>
                  <option value="booking">Prenotazione</option>
                  <option value="checkin">Check-in</option>
                  <option value="treatment">Trattamento</option>
                  <option value="class">Corso</option>
                  <option value="visit">Visita</option>
                  <option value="cancel">Cancellazione</option>
                  <option value="no_show">No-show</option>
                  <option value="loyalty">Loyalty</option>
                  <option value="coupon">Coupon</option>
                  <option value="notification">Notifica</option>
                  <option value="review">Recensione</option>
                </select>
              </div>
              <ul className="sk-enduser-feed">
                {history.map((h) => (
                  <li key={`${h.type}-${h.id}`} className="sk-enduser-feed__item">
                    <span className="sk-enduser-feed__icon">{HISTORY_ICON[h.type] || '•'}</span>
                    <div className="sk-enduser-feed__body">
                      <p className="sk-enduser-feed__action">
                        <strong>{HISTORY_LABEL[h.type] || h.type}</strong>
                        {h.vertical && h.vertical !== 'platform' ? ` · ${h.vertical}` : ''}
                        {' · '}
                        {h.store_name}
                        {h.category_code ? ` (${h.category_code})` : ''}
                      </p>
                      <p className="sk-enduser-feed__meta">
                        {h.description}
                        {h.amount_cents != null ? ` · ${h.amount_cents > 0 ? '+' : ''}${h.amount_cents} cents` : ''}
                        {h.status ? ` · ${h.status}` : ''}
                        {' · '}
                        {new Date(h.created_at).toLocaleString('it-IT')}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
